import { MODES, OPS, type Mode, type Op } from "../content/modes";
import type { BattleEnd, PlayEvent } from "../game/events";
import type { Answer } from "../game/round";
import { ProgressSchema } from "../save/schema";
import { z } from "zod";

/*
  The shapes the database speaks (snake_case rows and function arguments),
  and conversions to and from the game's events. Pure.
*/

export interface ChildRow {
  id: string;
  name: string;
  pin: number[] | null;
  settings: unknown;
  updated_at: string;
  deleted_at: string | null;
}

export interface AnswerRow {
  n: number;
  type_id: string;
  fact_key: string;
  correct: boolean;
  ms: number | null;
  /** The child tapped Pass. Missing on rows from before Pass existed. */
  passed?: boolean | null;
}

export interface SessionRow {
  id: string;
  seq: number;
  child_id: string;
  kind: "trial" | "practice" | "battle" | "baseline";
  mode: string | null;
  rank_index: number | null;
  score: number;
  tried: number;
  seconds: number;
  duration_ms: number | null;
  passed: boolean | null;
  audio: boolean;
  seed: number | null;
  ops: string[] | null;
  /** Which version of the trial numbers a trial was played on. */
  trials_version: number | null;
  /** Coins the round earned; null on rows from before coins. */
  coins: number | null;
  /** Battles only. */
  enemy: string | null;
  fighter: string | null;
  won: boolean | null;
  ending: string | null;
  points: number | null;
  damage: number | null;
  hits: number | null;
  move_used: boolean | null;
  battles_version: number | null;
  /** The kit a battle fought with, and the active items pressed. */
  items: unknown;
  items_used: string[] | null;
  payload: unknown;
  occurred_at: string;
  local_date: string;
  answers?: AnswerRow[];
}

export interface RankRow {
  id: string;
  seq: number;
  child_id: string;
  mode: string;
  rank_index: number;
  reason: "placement" | "parent" | "retune";
  ops: string[] | null;
  occurred_at: string;
  local_date: string | null;
}

export interface PurchaseRow {
  id: string;
  seq: number;
  child_id: string;
  item: string;
  level: number;
  price: number;
  shop_version: number;
  occurred_at: string;
  local_date: string | null;
}

/** An event as pulled from the server, with its place in the family's order. */
export interface PulledEvent {
  seq: number;
  childId: string;
  event: PlayEvent;
}

const isMode = (m: unknown): m is Mode => (MODES as readonly unknown[]).includes(m);
const isOp = (o: unknown): o is Op => (OPS as readonly unknown[]).includes(o);
const ENDS: readonly BattleEnd[] = ["won", "knockout", "timeout", "retreat"];
const isEnd = (e: unknown): e is BattleEnd => (ENDS as readonly unknown[]).includes(e);

const KitSchema = z.array(z.object({ id: z.string(), level: z.number().int().positive() }));

/** The kit on a session row, dropping anything that isn't one. */
function kitIn(raw: unknown): { id: string; level: number }[] {
  const r = KitSchema.safeParse(raw);
  return r.success ? r.data : [];
}

function answersOut(answers: Answer[]) {
  return answers.map((a) => ({
    type_id: a.typeId,
    fact_key: a.factKey,
    correct: a.correct,
    ms: a.ms,
    ...(a.passed ? { passed: true } : {}),
  }));
}

function answersIn(rows: AnswerRow[] | undefined): Answer[] {
  return [...(rows ?? [])]
    .sort((a, b) => a.n - b.n)
    .map((r) => ({
      typeId: r.type_id,
      factKey: r.fact_key,
      correct: r.correct,
      ms: r.ms,
      ...(r.passed ? { passed: true } : {}),
    }));
}

/** Arguments for record_session, record_rank_change or record_purchase. */
export function eventPayload(childId: string, deviceId: string, e: PlayEvent) {
  const common = { id: e.id, child_id: childId, device_id: deviceId, occurred_at: e.at };
  switch (e.kind) {
    case "trial":
      return {
        fn: "record_session" as const,
        body: {
          ...common,
          local_date: e.day,
          kind: "trial",
          mode: e.mode,
          rank_index: e.rankIndex,
          score: e.score,
          tried: e.tried,
          seconds: e.seconds,
          duration_ms: e.durationMs,
          passed: e.passed,
          audio: e.audio,
          seed: e.seed,
          trials_version: e.trialsVersion,
          coins: e.coins ?? 0,
          ...(e.ops ? { ops: e.ops } : {}),
          answers: answersOut(e.answers),
        },
      };
    case "practice":
      return {
        fn: "record_session" as const,
        body: {
          ...common,
          local_date: e.day,
          kind: "practice",
          mode: e.mode,
          score: e.score,
          tried: e.tried,
          seconds: e.seconds,
          duration_ms: e.durationMs,
          audio: e.audio,
          coins: e.coins ?? 0,
          ...(e.ops ? { ops: e.ops } : {}),
          answers: answersOut(e.answers),
        },
      };
    case "battle":
      return {
        fn: "record_session" as const,
        body: {
          ...common,
          local_date: e.day,
          kind: "battle",
          mode: e.mode,
          enemy: e.enemy,
          fighter: e.fighter,
          score: e.score,
          tried: e.tried,
          seconds: e.seconds,
          duration_ms: e.durationMs,
          points: e.points,
          damage: e.damage,
          hits: e.hits,
          won: e.won,
          ending: e.end,
          move_used: e.moveUsed,
          ...(e.items?.length ? { items: e.items } : {}),
          ...(e.itemsUsed?.length ? { items_used: e.itemsUsed } : {}),
          coins: e.coins,
          seed: e.seed,
          audio: e.audio,
          battles_version: e.battlesVersion,
          ...(e.ops ? { ops: e.ops } : {}),
          answers: answersOut(e.answers),
        },
      };
    case "baseline":
      return {
        fn: "record_session" as const,
        body: { ...common, local_date: e.day, kind: "baseline", payload: e.progress },
      };
    case "purchase":
      return {
        fn: "record_purchase" as const,
        body: {
          ...common,
          local_date: e.day,
          item: e.item,
          level: e.level,
          price: e.price,
          shop_version: e.shopVersion,
        },
      };
    case "rank":
      return {
        fn: "record_rank_change" as const,
        body: {
          ...common,
          local_date: e.day,
          mode: e.mode,
          rank_index: e.rankIndex,
          reason: e.reason,
          ...(e.ops ? { ops: e.ops } : {}),
        },
      };
  }
}

/** A session row back into an event, or null if it doesn't make sense. */
export function sessionEvent(r: SessionRow): PulledEvent | null {
  const base = { id: r.id, at: r.occurred_at, day: r.local_date };
  const ops = r.ops?.filter(isOp);
  let event: PlayEvent;
  if (r.kind === "baseline") {
    const progress = ProgressSchema.safeParse(r.payload);
    if (!progress.success) return null;
    event = { ...base, kind: "baseline", progress: progress.data };
  } else {
    if (!isMode(r.mode)) return null;
    const common = {
      ...base,
      mode: r.mode,
      score: r.score,
      tried: r.tried,
      seconds: r.seconds,
      audio: r.audio,
      durationMs: r.duration_ms,
      answers: answersIn(r.answers),
    };
    const coins = r.coins ?? 0;
    if (r.kind === "trial") {
      if (r.rank_index === null) return null;
      event = {
        ...common,
        kind: "trial",
        rankIndex: r.rank_index,
        passed: !!r.passed,
        seed: r.seed ?? 0,
        trialsVersion: r.trials_version ?? 1,
        coins,
        ...(ops ? { ops } : {}),
      };
    } else if (r.kind === "battle") {
      if (!r.enemy || !r.fighter || !isEnd(r.ending)) return null;
      event = {
        ...common,
        kind: "battle",
        enemy: r.enemy,
        fighter: r.fighter,
        points: r.points ?? 0,
        damage: r.damage ?? 0,
        hits: r.hits ?? 0,
        won: !!r.won,
        end: r.ending,
        moveUsed: !!r.move_used,
        ...(r.items ? { items: kitIn(r.items) } : {}),
        ...(r.items_used ? { itemsUsed: r.items_used } : {}),
        coins,
        seed: r.seed ?? 0,
        battlesVersion: r.battles_version ?? 1,
        ...(ops ? { ops } : {}),
      };
    } else {
      event = { ...common, kind: "practice", coins, ...(ops ? { ops } : {}) };
    }
  }
  return { seq: r.seq, childId: r.child_id, event };
}

export function rankEventIn(r: RankRow): PulledEvent | null {
  if (!isMode(r.mode)) return null;
  const ops = r.ops?.filter(isOp);
  return {
    seq: r.seq,
    childId: r.child_id,
    event: {
      id: r.id,
      at: r.occurred_at,
      day: r.local_date ?? r.occurred_at.slice(0, 10),
      kind: "rank",
      mode: r.mode,
      rankIndex: r.rank_index,
      reason: r.reason,
      ...(ops ? { ops } : {}),
    },
  };
}

export function purchaseEventIn(r: PurchaseRow): PulledEvent {
  return {
    seq: r.seq,
    childId: r.child_id,
    event: {
      id: r.id,
      at: r.occurred_at,
      day: r.local_date ?? r.occurred_at.slice(0, 10),
      kind: "purchase",
      item: r.item,
      level: r.level,
      price: r.price,
      shopVersion: r.shop_version,
    },
  };
}
